// Create Journey Page Component
// ==============================
// Full-page experience for creating a new learning journey

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { createTopic } from '../../services/firebase/topicsService';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Layout from '../layout/Layout';
import Breadcrumb, { BreadcrumbItem } from '../ui/Breadcrumb';
import type { ExamFormat } from '../../types';

// Extended type for form state that allows empty string
type EditExamFormat = ExamFormat | '';

const CreateJourneyPage: React.FC = () => {
  const { t } = useTranslation('library');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use centralized learning journeys hook (Feature #21 - Firebase CRUD)
  const { createJourney } = useLearningJourneys();

  // Form state
  const [subject, setSubject] = useState('');
  const [examDate, setExamDate] = useState('');
  const [examFormat, setExamFormat] = useState<EditExamFormat>('');
  const [weeklyStudyHours, setWeeklyStudyHours] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!subject.trim()) {
      setError(tErrors('validation.subjectRequired'));
      return;
    }

    setIsLoading(true);

    try {
      // Create journey in Firestore
      const result = await createJourney({
        subject: subject.trim(),
        examDate: examDate || undefined,
        examFormat: examFormat || undefined,
        weeklyStudyHours: weeklyStudyHours ? parseInt(weeklyStudyHours, 10) : undefined,
      });

      if (!result.success || !result.id) {
        setError(result.error || t('createJourney.couldNotCreate'));
        setIsLoading(false);
        return;
      }

      const journeyId = result.id;

      // Create initial topic in Firestore (subcollection under journey)
      const topicResult = await createTopic(journeyId, {
        title: subject.trim(),
        journeyId: journeyId,
        userId: user?.uid || '',
        order: 0,
        subtopics: [],
      });

      if (!topicResult.success) {
        console.error('[CreateJourneyPage] Failed to create topic:', topicResult.error);
        // Journey was created but topic failed — still navigate so user can retry
      }

      // Navigate directly to topic planner (Anna helps build the study plan)
      navigate(`/library/journey/${journeyId}`);
    } catch (err) {
      console.error('[CreateJourneyPage] Error creating journey:', err);
      setError(tErrors('generic'));
      setIsLoading(false);
    }
  };

  // Breadcrumbs
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: tCommon('nav.mySessions'), href: '/sessions' },
    { label: t('createJourney.breadcrumb') },
  ];

  return (
    <Layout>
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/sessions')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors md:hidden"
              aria-label={t('createJourney.backToTeaching', { defaultValue: tCommon('nav.mySessions') })}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-neutral-dark dark:text-white" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            <div>
              {/* Feature #280: Serif display font for headings */}
              <h1 className="text-2xl font-display font-bold text-neutral-dark dark:text-white">
                {t('createJourney.title')}
              </h1>
              <div className="mt-1">
                <Breadcrumb items={breadcrumbItems} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Introduction */}
        <div className="mb-8 text-center">
          <div className="text-4xl mb-4">📚</div>
          <p className="text-neutral-medium dark:text-gray-400">
            {t('createJourney.description')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6">
          {error && (
            <div
              className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
              role="alert"
            >
              <p className="text-red-600 dark:text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-neutral-dark dark:text-white mb-2">
                {t('createJourney.subjectLabel')} *
              </label>
              <Input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('createJourney.subjectPlaceholder')}
                required
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-neutral-medium">
                {t('createJourney.subjectHelp')}
              </p>
            </div>

            {/* Exam Date */}
            <div>
              <label htmlFor="examDate" className="block text-sm font-medium text-neutral-dark dark:text-white mb-2">
                {t('createJourney.examDateLabel')}
              </label>
              <Input
                id="examDate"
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-neutral-medium">
                {t('createJourney.examDateHelp')}
              </p>
            </div>

            {/* Exam Format */}
            <div>
              <label className="block text-sm font-medium text-neutral-dark dark:text-white mb-2">
                {t('createJourney.examFormatLabel')}
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setExamFormat('mundtlig')}
                  disabled={isLoading}
                  className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${
                    examFormat === 'mundtlig'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🎤</span>
                    <div>
                      <span className="font-medium text-neutral-dark dark:text-white">{t('createJourney.oral')}</span>
                      <p className="text-xs text-neutral-medium">{t('createJourney.oralDescription')}</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setExamFormat('skriftlig')}
                  disabled={isLoading}
                  className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${
                    examFormat === 'skriftlig'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📝</span>
                    <div>
                      <span className="font-medium text-neutral-dark dark:text-white">{t('createJourney.written')}</span>
                      <p className="text-xs text-neutral-medium">{t('createJourney.writtenDescription')}</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setExamFormat('multiple_choice')}
                  disabled={isLoading}
                  className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${
                    examFormat === 'multiple_choice'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">✅</span>
                    <div>
                      <span className="font-medium text-neutral-dark dark:text-white">{t('createJourney.multipleChoice')}</span>
                      <p className="text-xs text-neutral-medium">{t('createJourney.multipleChoiceDescription')}</p>
                    </div>
                  </div>
                </button>
              </div>
              <p className="mt-2 text-xs text-neutral-medium">
                {t('createJourney.examFormatHelp')}
              </p>
            </div>

            {/* Weekly Study Hours */}
            <div>
              <label htmlFor="weeklyHours" className="block text-sm font-medium text-neutral-dark dark:text-white mb-2">
                {t('createJourney.weeklyHoursLabel')}
              </label>
              <Input
                id="weeklyHours"
                type="number"
                min="1"
                max="40"
                value={weeklyStudyHours}
                onChange={(e) => setWeeklyStudyHours(e.target.value)}
                placeholder={t('createJourney.weeklyHoursPlaceholder')}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-neutral-medium">
                {t('createJourney.weeklyHoursHelp')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => navigate('/sessions')}
              disabled={isLoading}
            >
              {tCommon('actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? t('createJourney.creating') : t('createJourney.createButton')}
            </Button>
          </div>
        </form>

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-neutral-medium dark:text-gray-400">
            {t('createJourney.afterCreate')}
          </p>
        </div>
      </main>
    </Layout>
  );
};

export default CreateJourneyPage;
